import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  getInstance, getInstanceHistory,
  approveInstance, rejectInstance, returnInstance, recallInstance,
  holdInstance, resumeInstance, cancelInstance, delegateInstance,
  escalateInstance, forwardInstance, skipStep, reworkInstance,
  ESCALATION_ACTIONS,
  type WorkflowInstance, type ActionLogRow,
} from '../../api/workflowApi.js';
import { sx, StatusBadge, toOptions } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { ApprovalLevels, personLabel } from '../../components/ApprovalLevels.js';
import { UserPicker } from '../../components/UserPicker.js';

type ActionKey =
  | 'approve' | 'reject' | 'return' | 'recall' | 'hold' | 'resume' | 'cancel'
  | 'delegate' | 'escalate' | 'forward' | 'skip' | 'rework';

interface ActionSpec {
  readonly key: ActionKey;
  readonly danger?: boolean;
  readonly needsRemarks?: 'optional' | 'required';
  readonly needsUser?: boolean;
  readonly needsEscalation?: boolean;
  readonly needsGoto?: boolean;
}

const ACTIONS: readonly ActionSpec[] = [
  { key: 'approve', needsRemarks: 'optional' },
  { key: 'reject', danger: true, needsRemarks: 'required' },
  { key: 'return', needsRemarks: 'required' },
  { key: 'rework', needsRemarks: 'required', needsGoto: true },
  { key: 'recall', needsRemarks: 'optional' },
  { key: 'hold', needsRemarks: 'optional' },
  { key: 'resume' },
  { key: 'cancel', danger: true, needsRemarks: 'optional' },
  { key: 'delegate', needsUser: true, needsRemarks: 'optional' },
  { key: 'escalate', needsUser: true, needsEscalation: true, needsRemarks: 'optional' },
  { key: 'forward', needsUser: true, needsRemarks: 'optional' },
  { key: 'skip', needsRemarks: 'optional' },
];

/**
 * WorkflowInstanceDetailPage — runtime instance detail with the full action
 * panel (UI Audit M04 C3) and the immutable per-instance history trail
 * (UI Audit M04 C4, §23).
 *
 * Every runtime transition — approve / reject / return / rework / recall /
 * hold / resume / cancel / delegate / escalate / forward / skip — is driven
 * here against `/v1/wf/instances/:id/*`, with a config-driven inline form that
 * collects only the fields each action needs (remarks / target user /
 * escalation tier). The History section renders the T052 action log returned
 * by `GET …/history`.
 */
export function WorkflowInstanceDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [inst, setInst] = useState<WorkflowInstance | null>(null);
  const [history, setHistory] = useState<ActionLogRow[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [active, setActive] = useState<ActionSpec | null>(null);
  const [remarks, setRemarks] = useState('');
  const [userId, setUserId] = useState('');
  const [escAction, setEscAction] = useState('');
  const [gotoSeq, setGotoSeq] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    getInstance(token, id).then((r) => { setInst(r); setError(null); }).catch((e) => setError((e as Error).message));
    getInstanceHistory(token, id)
      .then((h) => { setHistory(h); setHistoryError(null); })
      // An unread log is not "no history".
      .catch((e) => setHistoryError((e as Error).message));
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  function begin(spec: ActionSpec): void {
    setActive(spec); setRemarks(''); setUserId(''); setEscAction(''); setGotoSeq(''); setError(null); setNotice(null);
  }

  async function confirm(): Promise<void> {
    if (!id || !active) return;
    if (active.needsRemarks === 'required' && !remarks.trim()) { setError(t('wf.instDetail.remarksRequired', { action: t(`wf.instDetail.verb.${active.key}`) })); return; }
    if (active.needsUser && !userId) { setError(t('wf.instDetail.targetUserReq')); return; }
    setBusy(true); setError(null);
    try {
      switch (active.key) {
        case 'approve': await approveInstance(token, id, remarks.trim() || undefined); break;
        case 'reject': await rejectInstance(token, id, remarks.trim()); break;
        case 'return': await returnInstance(token, id, remarks.trim()); break;
        case 'recall': await recallInstance(token, id, remarks.trim() || undefined); break;
        case 'hold': await holdInstance(token, id, remarks.trim() || undefined); break;
        case 'resume': await resumeInstance(token, id); break;
        case 'cancel': await cancelInstance(token, id, remarks.trim() || undefined); break;
        case 'skip': await skipStep(token, id, remarks.trim() || undefined); break;
        case 'delegate': await delegateInstance(token, id, { toUserId: userId, reason: remarks.trim() || undefined }); break;
        case 'forward': await forwardInstance(token, id, { toUserId: userId, remarks: remarks.trim() || undefined }); break;
        case 'escalate': await escalateInstance(token, id, {
          escalatedToUserId: userId,
          escalationAction: escAction ? (escAction as (typeof ESCALATION_ACTIONS)[number]) : undefined,
          remarks: remarks.trim() || undefined,
        }); break;
        case 'rework': await reworkInstance(token, id, {
          remarks: remarks.trim(),
          goToStepSequence: gotoSeq ? Number(gotoSeq) : undefined,
        }); break;
      }
      setNotice(t('wf.instDetail.actionDone', { action: t(`wf.instDetail.action.${active.key}`) }));
      setActive(null);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="wf-instance-detail" style={sx.main}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Link to="/admin/wf/instances" style={sx.link}>{t('wf.instDetail.backInstances')}</Link>
        <h1>{t('wf.instDetail.title')}</h1>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {!inst ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="instance-summary" style={{ ...sx.card }}>
            <p data-testid="inst-ref">
              <strong>{inst.instanceRefNumber ?? '—'}</strong>{' '}
              <StatusBadge status={inst.instanceStatus} />
            </p>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              {t('wf.common.entityColon')} {inst.entityType}{inst.entityRefNumber ? ` · ${inst.entityRefNumber}` : ''}
              {inst.submittedAt ? ` · ${t('wf.instDetail.submitted')} ${at(inst.submittedAt)}` : ''}
              {inst.completedAt ? ` · ${t('wf.instDetail.completed')} ${at(inst.completedAt)}` : ''}
            </p>
          </section>

          <section aria-label="instance-steps" style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 16 }}>{t('wf.common.steps')}</h2>
            {/* G18: each level names its approvers and who acted (stepTrail), never a user uuid. */}
            {!inst.stepTrail || inst.stepTrail.length === 0 ? (
              <p data-testid="inst-steps-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.instDetail.noStepActivity')}</p>
            ) : (
              <ApprovalLevels steps={inst.stepTrail} />
            )}
          </section>

          <section aria-label="action-panel" style={{ ...sx.card }}>
            <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('wf.common.actions')}</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ACTIONS.map((a) => (
                <Button key={a.key} type="button"
                  variant={a.danger ? 'danger' : 'secondary'}
                  aria-label={`action-${a.key}`} onClick={() => begin(a)}>{t(`wf.instDetail.action.${a.key}`)}</Button>
              ))}
            </div>

            {active && (
              <div aria-label="action-form" style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--color-border-default)' }}>
                <h3 style={{ fontSize: 14 }}>{t(`wf.instDetail.action.${active.key}`)}</h3>
                {active.needsUser && (
                  <div>
                    <UserPicker label={t('wf.instDetail.targetUserLabel')} testId="action-user" value={userId} onPick={(uid) => setUserId(uid)} />
                  </div>
                )}
                {active.needsEscalation && (
                  <div>
                    <label htmlFor="action-esc" style={sx.label}>{t('wf.instDetail.escActionLabel')}</label>
                    <select id="action-esc" aria-label="action-esc" value={escAction}
                      onChange={(e) => setEscAction(e.target.value)} style={sx.input}>
                      <option value="">{t('wf.instDetail.defaultOption')}</option>
                      {toOptions(ESCALATION_ACTIONS).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                )}
                {active.needsGoto && (
                  <div>
                    <label htmlFor="action-goto" style={sx.label}>{t('wf.instDetail.gotoLabel')}</label>
                    <input id="action-goto" aria-label="action-goto" type="number" value={gotoSeq}
                      onChange={(e) => setGotoSeq(e.target.value)} style={sx.input} />
                  </div>
                )}
                {active.needsRemarks && (
                  <div>
                    <label htmlFor="action-remarks" style={sx.label}>
                      {t('wf.instDetail.remarks')}{active.needsRemarks === 'required' ? ' *' : ''}
                    </label>
                    <textarea id="action-remarks" aria-label="action-remarks" value={remarks}
                      onChange={(e) => setRemarks(e.target.value)} rows={3} style={{ ...sx.input, fontFamily: 'inherit' }} />
                  </div>
                )}
                <div style={sx.actionBar}>
                  <Button type="button" variant="primary" disabled={busy} aria-label="action-confirm" onClick={confirm}>
                    {busy ? t('wf.instDetail.working') : t('wf.instDetail.confirm', { action: t(`wf.instDetail.action.${active.key}`) })}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setActive(null)}>{t('common.cancel')}</Button>
                </div>
              </div>
            )}
          </section>

          <section aria-label="history-viewer">
            <h2 style={{ fontSize: 16 }}>{t('wf.instDetail.history')}</h2>
            {historyError ? (
              <p role="alert" data-testid="history-error" style={sx.error}>{historyError}</p>
            ) : !history ? <p>{t('common.loading')}</p> : history.length === 0 ? (
              <p data-testid="history-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.instDetail.noHistory')}</p>
            ) : (
              <DataTable<ActionLogRow>
                ariaLabel="history-table"
                rows={history}
                rowKey={(h) => h.actionLogId}
                rowTestId={(h) => `history-${h.actionLogId}`}
                columns={[
                  { key: 'when', header: t('wf.common.when'), render: (h) => (h.actionTimestamp ? at(h.actionTimestamp) : '—') },
                  { key: 'action', header: t('wf.common.action'), render: (h) => h.actionType },
                  { key: 'actor', header: t('wf.instDetail.colActor'), render: (h) => personLabel(h.actorName ?? null, h.actorCode ?? null) ?? '—' },
                  { key: 'transition', header: t('wf.instDetail.colTransition'), render: (h) => `${h.fromStatus ?? '—'} → ${h.toStatus ?? '—'}` },
                  { key: 'remarks', header: t('wf.instDetail.remarks'), render: (h) => h.remarks ?? '—' },
                ]}
              />
            )}
          </section>
        </>
      )}
    </main>
  );
}
